// The shell's own words (G4A): the brand lockup, the top bar, the navigation landmark, and the words the gallery's shell
// demonstrations draw (his page-title locators, his tab sets, his console areas). Each quotes the line of the vendored
// glossary or design-system excerpts it is taken from; labels.test.mjs checks every quote, text and status. Navigation
// item names, role names and status words come from the shared module (NAV_ITEM_LABELS, ROLE_LABELS, the chains).

import { defineLabels, template, type LabelSource } from "../labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const;

const BRAND_ROW = "GoToFoods mark · *Talent Acquisition Hiring Hub* (DC-229; short form *Hiring Hub*)";
const TOPBAR_ROW = "breadcrumb in his screen names · *Search* · **?** = *Process Guide* · notifications (Q-E19)";
const BELL_STATE = "H ibtn lift · F std · bell `.dot` red when unread";
const NAV_ROW = "| 1 | **Navigation** |";
const TITLES = "page titles = his locators (*Intake Part 2*, *Kickoff & Packets*, *Offer package — {candidate}*)";
const TAB_WORDS = "*Prioritized actions · History* · *Pending Review · Advanced · Held · Passed* · *Compensation · Offer*";
const CONSOLE_AREAS = "*Statuses · Reason lists · Taxonomies · Thresholds · Reminders · Templates · Segments · Strategies*";

/** The shell's chrome: the lockup, the navigation landmark, the top bar's controls and the bell's unread state. */
export const SHELL_LABELS = /* @__PURE__ */ defineLabels({
  brandTitle: his("Talent Acquisition Hiring Hub"),
  mark: his("GoToFoods"),
  navigation: his("Navigation"),
  breadcrumb: his("breadcrumb"),
  search: his("Search"),
  processGuide: his("Process Guide"),
  notifications: his("notifications"),
  unread: his("unread"),
});

/**
 * The assistant panel (UAT F-146): the top bar's button and the panel's name, and the placeholder card the panel holds
 * until its content is built. *Assistant* and *The assistant panel* are Tejesh's line (12 Oct 05:54, "an AI button …
 * the sidebar opens up", ruled his on 12 Oct), quoting the glossary's name for the panel in the CR-072 row (G §2, "the
 * assistant panel"). The card's sentence is his own (G §1, the advisory row). No vendor, model or product is named
 * (DC-33).
 */
const ASSISTANT_ROW = "the assistant panel";
export const ASSISTANT_LABELS = /* @__PURE__ */ defineLabels({
  assistant: his("Assistant"),
  panel: his("The assistant panel"),
  humansDecide: his("Humans make employment decisions."),
});

/** Page titles (his screen locators, DS #5) and the tab sets and console areas the shell's tabs and rail carry. */
export const SHELL_PAGE_LABELS = /* @__PURE__ */ defineLabels({
  intakePart2: his("Intake Part 2"),
  kickoffPackets: his("Kickoff & Packets"),
  prioritizedActions: his("Prioritized actions"),
  history: his("History"),
  pendingReview: his("Pending Review"),
  advanced: his("Advanced"),
  held: his("Held"),
  passed: his("Passed"),
  compensation: his("Compensation"),
  offer: his("Offer"),
  statuses: his("Statuses"),
  reasonLists: his("Reason lists"),
  taxonomies: his("Taxonomies"),
  thresholds: his("Thresholds"),
  reminders: his("Reminders"),
  templates: his("Templates"),
  segments: his("Segments"),
  strategies: his("Strategies"),
  // His CR-066 name is "TA Admin configuration console"; DC-228 folds TA Admin into the one Admin, so the name reads
  // with the Admin word (a run of his own words).
  configurationConsole: his("Admin configuration console"),
});

/** "Offer package — {candidate}" (DS #5): a page title with the candidate's name. */
export const SHELL_TEMPLATES = {
  offerPackage: /* @__PURE__ */ template(his("Offer package — {candidate}")),
} as const;
